import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ScheduledEvent } from '../event-details';
import type { BitratePoint, EventPhase, MuxHealth, MuxRange } from './mux-health';
import { bitrateIncidents, buildMockHealth, EMPTY_HEALTH, MUX_BASE, USE_MOCK } from './mux-health';

/*
 * Live Mux health for the Home tab. Fetches the real endpoints (ingest-health +
 * mux-metrics) for a live event and maps them to `MuxHealth`. The ingest window
 * follows the chart's selected range (Live=5m … Full=from event start) and is
 * polled every 10s; viewer/quality metrics are polled every 30s. Falls back to
 * the mock (buildMockHealth) when not live, before data arrives, on error, or
 * when USE_MOCK is on. Swap point (MUX_BASE / USE_MOCK) lives in mux-health.ts.
 */

const INGEST_POLL_MS = 10_000;
const METRICS_POLL_MS = 30_000;
// Ingest start barely moves; slow poll just catches an encoder that started late.
const START_POLL_MS = 60_000;

const RANGE_MINUTES: Record<Exclude<MuxRange, 'full'>, number> = { live: 5, '15m': 15, '1h': 60 };

interface IngestHealthResponse {
  online: boolean;
  videoKbps: number | null;
  audioKbps: number | null;
  fps: number | null;
  bitrateSeries: number[];
  bitrateTimes: string[];
}

interface MuxMetricsResponse {
  viewers: number;
  qualityScore: number | null;
  currentQuality: number | null;
}

export interface MuxHealthState {
  health: MuxHealth;
  loading: boolean;
  error?: string;
}

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return <T>await response.json();
};

const ingestUrl = (liveStreamId: string, range: MuxRange, startTime: string): string => {
  const id = encodeURIComponent(liveStreamId);

  if (range === 'full') {
    const start = DateTime.fromISO(startTime);

    // Whole event since it started; wide fallback when the start time is missing.
    return start.isValid
      ? `${MUX_BASE}/ingest-health?liveStreamId=${id}&from=${Math.floor(start.toSeconds())}`
      : `${MUX_BASE}/ingest-health?liveStreamId=${id}&minutes=720`;
  }

  return `${MUX_BASE}/ingest-health?liveStreamId=${id}&minutes=${RANGE_MINUTES[range]}`;
};

const toSeries = (values: number[], times: string[]): BitratePoint[] =>
  values.map((kbps, i) => ({ time: times[i] ?? '', kbps }));

const mapHealth = (ingest: IngestHealthResponse, metrics?: MuxMetricsResponse): MuxHealth => ({
  online: ingest.online,
  videoKbps: ingest.videoKbps ?? 0,
  audioKbps: ingest.audioKbps ?? 0,
  fps: ingest.fps ?? 0,
  viewers: metrics?.viewers ?? 0,
  qualityScore: metrics?.qualityScore ?? metrics?.currentQuality ?? 0,
  // Filled by withIncidents below, off the series that ends up on screen.
  dips: 0,
  drops: 0,
  series: toSeries(ingest.bitrateSeries, ingest.bitrateTimes)
});

// Fills dips/drops from whatever series the caller ended up with.
const withIncidents = (health: MuxHealth): MuxHealth => {
  const { dips, drops } = bitrateIncidents(health.series);

  return { ...health, dips, drops };
};

export const useMuxHealth = (
  scheduledEvent: ScheduledEvent,
  phase: EventPhase,
  range: MuxRange
): MuxHealthState => {
  const live = phase === 'live';
  const past = phase === 'past';
  const mock = useMemo(() => buildMockHealth(), []);
  const [real, setReal] = useState<MuxHealth>();
  const [streamStartMs, setStreamStartMs] = useState<number>();
  /*
   * Starts true so the first render reads as "still asking Mux" rather than
   * "Mux has nothing": effects only run after the first paint, and callers key
   * their empty state off this.
   */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const ingestRef = useRef<IngestHealthResponse>();
  const metricsRef = useRef<MuxMetricsResponse>();

  const liveStreamId = scheduledEvent.location?.mux_stream_id ?? '';
  const title = scheduledEvent.name;
  // start_time is typed `any` (AWSDateTime); coerce to string safely.
  const startTime = typeof scheduledEvent.start_time === 'string' ? scheduledEvent.start_time : '';
  const active = !USE_MOCK && (live || past) && liveStreamId.length > 0;

  // Ingest series (instant bitrate/fps + chart data) — window follows the range, polled fast.
  useEffect(() => {
    if (!active) {
      setLoading(false);

      return undefined;
    }

    let cancelled = false;
    // A finished event has one meaningful window: the event itself.
    const url = ingestUrl(liveStreamId, past ? 'full' : range, startTime);

    const run = async (): Promise<void> => {
      /*
       * Skipping while hidden keeps the live poll from burning a request every 10s
       * in a background tab, and the next tick picks it up. A past event only ever
       * fetches ONCE, though: skipping it there means it never runs at all, and the
       * tab sits on "Loading" forever. Opening an event in a new tab does exactly
       * that, since a background tab starts hidden.
       */
      if (!past && document.hidden) {
        return;
      }

      try {
        const ingest = await fetchJson<IngestHealthResponse>(url);

        if (!cancelled) {
          ingestRef.current = ingest;
          setReal(mapHealth(ingest, metricsRef.current));
          setError(undefined);
        }
      } catch (error_: unknown) {
        if (!cancelled) {
          setError(error_ instanceof Error ? error_.message : 'Failed to load Mux health');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setLoading(true);
    void run();

    // Nothing moves once the event is over: fetch once and stop.
    if (past) {
      return (): void => {
        cancelled = true;
      };
    }

    const timer = setInterval((): void => {
      void run();
    }, INGEST_POLL_MS);

    return (): void => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, liveStreamId, past, range, startTime]);

  /*
   * Ingest start, for Uptime. The API doesn't return it (see
   * docs/mux-ingest-health-spec.md), so we read it off the first sample of the
   * WHOLE-event window. It has to be its own request: the main one follows the
   * chart's range, so with "Live" selected its first sample is 5 minutes ago and
   * Uptime would read 5:00 no matter how long the stream has really been up.
   */
  useEffect(() => {
    if (!active || !live) {
      return undefined;
    }

    let cancelled = false;
    const url = ingestUrl(liveStreamId, 'full', startTime);

    const run = async (): Promise<void> => {
      if (document.hidden) {
        return;
      }

      try {
        const ingest = await fetchJson<IngestHealthResponse>(url);
        const first = ingest.bitrateTimes[0] ?? '';
        const startedAt = DateTime.fromISO(first);

        if (!cancelled && startedAt.isValid) {
          setStreamStartMs(startedAt.toMillis());
        }
      } catch {
        // Uptime is optional enrichment; leave it blank rather than guessing.
      }
    };

    void run();

    const timer = setInterval((): void => {
      void run();
    }, START_POLL_MS);

    return (): void => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, live, liveStreamId, startTime]);

  // Viewer/quality metrics — per-minute data, polled slower and independent of the range.
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let cancelled = false;

    const url = `${MUX_BASE}/mux-metrics?title=${encodeURIComponent(
      title
    )}&liveStreamId=${encodeURIComponent(liveStreamId)}&minutes=60`;

    const run = async (): Promise<void> => {
      if (document.hidden) {
        return;
      }

      try {
        const metrics = await fetchJson<MuxMetricsResponse>(url);

        if (!cancelled) {
          metricsRef.current = metrics;

          if (ingestRef.current) {
            setReal(mapHealth(ingestRef.current, metrics));
          }
        }
      } catch {
        // Metrics are optional enrichment; ignore failures here.
      }
    };

    void run();

    const timer = setInterval((): void => {
      void run();
    }, METRICS_POLL_MS);

    return (): void => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, past, liveStreamId, title]);

  const health = useMemo((): MuxHealth => {
    if (USE_MOCK) {
      return mock;
    }

    /*
     * A finished event shows what Mux still has, or nothing at all -- never the
     * mock. Mux ages out ingest data after about a day, and a fabricated curve on
     * a past event reads as a real record of how it went.
     */
    /*
     * Not clipped to end_time: the response only carries samples where ingest
     * actually happened — no zero padding up to now — and streams routinely run
     * past their scheduled end. Clipping cut that tail off, which lost both the
     * real last bitrate and any dip that happened after the scheduled end.
     */
    if (past) {
      return real ? withIncidents(real) : EMPTY_HEALTH;
    }

    /*
     * Live falls back to zeroes, not the mock: the mock's last sample (~2594 Kbps)
     * showed for a moment on every mount, so switching tabs flashed a plausible
     * bitrate belonging to nobody before the real one replaced it. The mock is
     * only for USE_MOCK now.
     */
    const base = real ?? EMPTY_HEALTH;

    return withIncidents(streamStartMs === undefined ? base : { ...base, streamStartMs });
  }, [mock, real, past, streamStartMs]);

  return { health, loading, error };
};
